import { buildMissedLinks, missedLinkStateLabel } from '@/domain/missedLinks';
import { lapsedMoveBookings } from '@/domain/topupMoveLapse';
import { buildSlotTakenAlerts } from '@/domain/slotTakenAlerts';
import { clientMessagedAfterPush, isLiveBooking } from '@atink/domain';
import { startOfNextStudioDayIso } from '@/domain/calendarWeek';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import QueueDialer from '@/components/queue/QueueDialer';
import QueueWorkModal from '@/components/queue/QueueWorkModal';
import SweeperWorkspace from '@/components/queue/SweeperWorkspace';
import ArtistAvailabilityHeatmap from '@/components/queue/ArtistAvailabilityHeatmap';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';
import {
  buildQueue,
  buildHeatmap,
  laneOf,
  partitionQueue,
  type GapFilter,
  type HeatmapCell,
  type QueueChannel,
  type QueueItem,
} from '@/domain/queueEngine';
import type { Channel, ConversationEntry, CustomerRecord } from '@/domain/types';
import { studioScopeIds } from '@/domain/studioScope';
import { relativeFromIso, simTimeShort } from '@/utils/format';
import {
  Target,
  XCircle,
  Phone,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Lock,
  Bell,
  Clock,
  PhoneMissed,
  CircleDollarSign,
  CheckCircle2,
  Ban,
  CalendarX,
} from 'lucide-react';
import { recommendChannel } from '@/domain/channelRecommendation';
import { format, startOfDay, addDays } from 'date-fns';
import clsx from 'clsx';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api/queryClient';
import { useAvailabilityView } from '@/api/views';

// Mirrors the composer's private channel union so we can forward the focused
// channel straight through to the work modal without widening the type. A
// QueueChannel minus 'voice' is exactly this set.
type MessageChannel = 'sms' | 'meta-dm' | 'ig-dm' | 'email';

// Primary sequence tabs, driven by each item's sequence-clock stage / red state
// (see stageOf). First/Second/Third are the three call-sequence attempts; Reds
// collects the terminal states (never-reached + said-no).
type SeqTab = 'first' | 'second' | 'third' | 'reds';

const SEQ_TABS: Array<{ key: SeqTab; label: string }> = [
  { key: 'first', label: 'First' },
  { key: 'second', label: 'Second' },
  { key: 'third', label: 'Third' },
  { key: 'reds', label: 'Reds' },
];

const SEQ_LABEL: Record<SeqTab, string> = {
  first: 'First',
  second: 'Second',
  third: 'Third',
  reds: 'Reds',
};

// The agent works a small, finite working set at a time rather than the whole
// agent-lane list (bd tv-6s3, board 05). As items clear (leave the derived
// queue) the next tail item fills in; when the whole list clears, buildQueue
// re-derives a fresh batch. Replies + missed calls surface in as they arrive.
const WORKING_SET_SIZE = 6;

// How long the "sequence cleared" success banner shows before the next sequence
// loads automatically (bd tv-1x7). UI timing, not domain time.
export const HANDOFF_DELAY_MS = 2200;

// A reactive event on the Alerts tab (bd tv-3wr.2). 'no-deposit-declined' (bd
// tv-aj0) is fed from active NoDepositDeclineFlags: Ops declined a no-money
// override, so the agent must chase an actual deposit.
type AlertKind =
  | 'no-deposit-declined'
  | 'missed-call'
  // W5 (DECIDED 24 Sep): an unpaid deposit link the AGENT sent -> call back.
  | 'unpaid-link'
  | 'missed-topup'
  // B4 (75my): a top-up date move whose partial wasn't paid — both dates released.
  | 'move-lapsed'
  // B5 (8xar; Anas 1 Oct): a named self-book link expired unpaid — call back and
  // send a fresh link (the artist is not told).
  | 'self-book-expired'
  // D02-07 / D05-19 (Anas 6 Oct): an overbooked this-week slot another client
  // paid for first — the server released this client's hold; call back for a new date.
  | 'date-taken'
  | 'new-reply'
  // Anas 8 Oct (enquiries-9): a pushed lead is the Agent's; the client DMed
  // (IG/FB) after the push and nobody has answered — the Agent replies.
  | 'client-messaged'
  | 'nurture-callback'
  | 'nurture-reply';

interface AlertRow {
  id: string;
  alertKind: AlertKind;
  customer: CustomerRecord;
  detail: string;
  whenIso?: string;
  action: 'call' | 'chase' | 'open';
  /** The channel the work modal's composer opens on (a DM is answered on its own channel). */
  channel?: MessageChannel;
}

const PROMOTED_KEY = 'ati-queue-promoted-alerts';

/** A work card for an alert whose customer has no queue card of their own (B8). */
function alertItem(row: AlertRow): QueueItem {
  const rec = recommendChannel(row.customer);
  const kind: QueueItem['kind'] =
    row.alertKind === 'missed-call' || row.alertKind === 'nurture-callback'
      ? 'missed-call'
      : row.alertKind === 'missed-topup'
        ? 'missed-topup'
        : row.alertKind === 'unpaid-link'
          ? 'pending-deposit'
          : 'unread-message';
  const depositId = row.alertKind === 'unpaid-link' ? row.id.replace(/^alert-unpaid-link-/, '') : undefined;
  return {
    id: `q-alert-${row.id}`,
    kind,
    band: 'today',
    cohort: 'fresh',
    customer: row.customer,
    reason: ALERT_META[row.alertKind].label,
    detail: row.detail,
    recommendedChannel: row.channel ?? (row.action === 'call' && row.customer.phone ? 'voice' : rec.channel),
    channelReason: row.channel ? 'they messaged you there' : rec.reason,
    channelConfidence: rec.confidence,
    recommendedAt: row.whenIso,
    priority: 800,
    ...(depositId ? { depositId, depositExpired: true } : {}),
  };
}

const ALERT_META: Record<AlertKind, { label: string; action: string }> = {
  'no-deposit-declined': { label: 'No-deposit declined', action: 'Collect deposit' },
  'missed-call': { label: 'Missed call', action: 'Call back' },
  'unpaid-link': { label: 'Unpaid deposit link', action: 'Call back' },
  'missed-topup': { label: 'Top-up missed', action: 'Chase' },
  'move-lapsed': { label: 'Date move lapsed', action: 'Call back' },
  'self-book-expired': { label: 'Self-book link expired', action: 'Send new link' },
  'date-taken': { label: 'Date taken', action: 'Find a new date' },
  'new-reply': { label: 'New reply', action: 'Open' },
  'client-messaged': { label: 'Client messaged', action: 'Reply' },
  'nurture-callback': { label: 'Nurture call-back', action: 'Call back' },
  'nurture-reply': { label: 'Nurture reply', action: 'Open' },
};

/**
 * Which sequence tab an item belongs to. Items with no sequence-clock state fall
 * to stage 1 (First); any red state (either red kind, or a bare 'red' stage)
 * lands in Reds.
 */
function stageOf(item: QueueItem): SeqTab {
  if (item.redKind || item.stage === 'red') return 'reds';
  const stage = item.stage ?? 1;
  return stage === 3 ? 'third' : stage === 2 ? 'second' : 'first';
}

// Secondary filters WITHIN the active sequence tab, over the AGENT-lane main list
// only (bd tv-570). AGENT_KIND_GROUPS lives in the shared kindGroups module: the
// money and open-business chips moved to the Sweeper, so only the fresh-lane
// Messages chip remains here. Chips hide when their count in the tab is 0.

function OperatorQueueInner() {
  const navigate = useNavigate();
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const pieces = useBrainStore((s) => s.pieces);
  const artists = useBrainStore((s) => s.artists);
  const rebookFollowupTasks = useBrainStore((s) => s.rebookFollowupTasks);
  const healingCheckTasks = useBrainStore((s) => s.healingCheckTasks);
  const topupPlans = useBrainStore((s) => s.topupPlans);
  const studios = useBrainStore((s) => s.studios);
  const sequenceState = useBrainStore((s) => s.sequenceState);
  const noDepositDeclineFlags = useBrainStore((s) => s.noDepositDeclineFlags);
  const selfBookLinks = useBrainStore((s) => s.selfBookLinks);
  const nurtureInbounds = useBrainStore((s) => s.nurtureInbounds);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const activeStudio = useSessionStore((s) => s.activeStudio);
  const clockIso = useSimulationStore((s) => s.clockIso);

  // The agent works one studio at a time (board 05). Scope every surface to the
  // active studio from the top-bar switcher, falling back to all assigned.
  // 'all' (or nothing picked) → the persona's whole network; a concrete studio →
  // just that one.
  // branding-3 (L6 review #4): "all" leaves archived studios out.
  const scopedStudios = useMemo(
    () => studioScopeIds(activeStudio, assignedStudios, studios),
    [activeStudio, assignedStudios, studios],
  );

  const [gapFilter, setGapFilter] = useState<GapFilter | null>(null);
  const [seqTab, setSeqTab] = useState<SeqTab>('first');
  const [showAvailability, setShowAvailability] = useState(false);
  const [modalItem, setModalItem] = useState<QueueItem | null>(null);
  const [modalChannel, setModalChannel] = useState<MessageChannel | undefined>(undefined);
  // The agent's gap-fill surface: this week's 3+ gap artists, own studios
  // (boards 05 + 08, tv-2na.13.14).
  const [agentGaps, setAgentGaps] = useState(false);
  // Alerts tab: reactive event-driven items collect here, out of the working set
  // (bd tv-3wr.2). Fifth tab alongside First/Second/Third/Reds.
  const [showAlerts, setShowAlerts] = useState(false);
  // Which sequences have already auto-advanced once. A sequence auto-advances the
  // FIRST time it clears; coming back to it later (by choice, or because new
  // leads landed and were worked) must not auto-advance again (bd tv-1x7).
  const [autoAdvanced, setAutoAdvanced] = useState<Record<SeqTab, boolean>>({
    first: false,
    second: false,
    third: false,
    reds: false,
  });

  const queue = useMemo(
    () =>
      buildQueue({
        customers,
        bookings,
        deposits,
        pieces,
        artists,
        rebookFollowupTasks,
        healingCheckTasks,
        topupPlans,
        studioIds: scopedStudios,
        nowIso: clockIso,
        gapFilter: gapFilter ?? undefined,
        sequenceState,
      }),
    [
      customers,
      bookings,
      deposits,
      pieces,
      artists,
      rebookFollowupTasks,
      healingCheckTasks,
      topupPlans,
      scopedStudios,
      clockIso,
      gapFilter,
      sequenceState,
    ],
  );

  // Live-read availability for the heatmap only (tv-1vh.10) — `buildQueue`
  // above stays on the raw, wide-scope `bookings` from the store (it spans
  // rebook follow-ups/healing checks far outside any one week). The live
  // per-studio view endpoint only covers one studio at a time, so this only
  // applies when the agent has narrowed to a single active studio (the
  // common case — "the agent works one studio at a time"); with several
  // studios still in scope it falls back to the original store-wide read.
  const singleScopedStudio = scopedStudios.length === 1 ? scopedStudios[0] : undefined;
  const heatmapFromIso = useMemo(() => startOfDay(new Date(clockIso)).toISOString(), [clockIso]);
  const heatmapToIso = useMemo(() => addDays(new Date(heatmapFromIso), 14).toISOString(), [heatmapFromIso]);
  const { data: heatmapAvailability } = useAvailabilityView(singleScopedStudio, heatmapFromIso, heatmapToIso);
  const heatmapBookings = singleScopedStudio ? (heatmapAvailability?.bookings ?? {}) : bookings;
  const heatmapRows = useMemo(
    () => buildHeatmap({ artists, bookings: heatmapBookings, nowIso: clockIso, studioIds: scopedStudios }),
    [artists, heatmapBookings, clockIso, scopedStudios],
  );

  // Standard-mode sections. In gap mode the queue is a flat candidate list and
  // this partition is unused.
  const { scheduledDue, main, scheduledCalls } = useMemo(
    () => partitionQueue(gapFilter ? [] : queue, clockIso),
    [queue, gapFilter, clockIso],
  );

  // The Agent works only its own lane (bd tv-570): fresh inquiries and pushed
  // leads. The money + open-business tail (deposits, unfinished pieces, rebooks,
  // healing checks) is Sweeper work and is filtered out of every agent surface
  // below — counts, tabs, chips and reds all read from agentMain.
  const agentMain = useMemo(
    () => main.filter((item) => laneOf(item.kind) === 'agent'),
    [main],
  );

  // Per-sequence-tab counts over the whole agent-lane main list.
  const seqCounts = useMemo(() => {
    const counts: Record<SeqTab, number> = { first: 0, second: 0, third: 0, reds: 0 };
    for (const item of agentMain) counts[stageOf(item)] += 1;
    return counts;
  }, [agentMain]);

  // Agent-lane items in the active (non-red) sequence tab.
  const mainForTab = useMemo(
    () => agentMain.filter((item) => stageOf(item) === seqTab),
    [agentMain, seqTab],
  );

  // Split the active sequence tab: leads still to call vs. contacted leads now
  // in their 24h cooldown ("completed this sequence", bd tv-jpx).
  const activeForTab = useMemo(() => mainForTab.filter((i) => !i.dormant), [mainForTab]);
  const completedForTab = useMemo(() => mainForTab.filter((i) => i.dormant), [mainForTab]);

  // Reds tab is split into two spaces: light reds are still callable, dark reds
  // (said-no) are handed to nurture and rendered read-only. Both read
  // from the agent lane, so a money-cohort red belongs to the Sweeper, not here.
  const lightReds = useMemo(() => agentMain.filter((i) => i.redKind === 'never-reached'), [agentMain]);
  const darkReds = useMemo(() => agentMain.filter((i) => i.redKind === 'said-no'), [agentMain]);

  // "Replies to action" rail: queued people (a sequence record exists) whose latest
  // message is an unanswered inbound reply, newest first. Fully derived, so a row
  // clears itself the moment the agent responds (the latest entry becomes outbound).
  const repliesToAction = useMemo(() => {
    const rows: Array<{ customer: CustomerRecord; entry: ConversationEntry }> = [];
    for (const [customerId, seq] of Object.entries(sequenceState)) {
      const c = customers[customerId];
      if (!c) continue;
      // A manually-pushed lead's latest inbound is the conversation it was pushed
      // FROM, not a fresh reactive reply. It starts the First sequence as a
      // pushed-lead card in the working set, so it must NOT also collect on the
      // Alerts tab as a "new reply" (bd tv-r41).
      if (seq.sourceConversationId) continue;
      const last = c.conversation[c.conversation.length - 1];
      if (!last || last.direction !== 'in') continue;
      rows.push({ customer: c, entry: last });
    }
    rows.sort((a, b) => b.entry.timestamp.localeCompare(a.entry.timestamp));
    return rows;
  }, [sequenceState, customers]);

  // Alerts feed (bd tv-3wr.2): the reactive, event-driven items — missed calls,
  // missed top-ups and new inbound replies. They collect on the Alerts tab as
  // they happen rather than silently reshuffling the six-item working set, then
  // batch in when the agent promotes them. One row per customer (missed-call
  // beats missed-top-up beats new-reply if the same person triggers two).
  const alerts = useMemo<AlertRow[]>(() => {
    const byCustomer = new Map<string, AlertRow>();

    // No-deposit declines (bd tv-aj0) — Ops declined a no-money override, so the
    // agent must chase an actual deposit (no second trip through Ops). Active
    // (unresolved) flags only; resolving one (deposit paid) drops the row on the
    // next read. Set first so a decline outranks any other event for the same
    // person — it is money-blocking.
    for (const flag of Object.values(noDepositDeclineFlags)) {
      if (flag.resolvedAt) continue;
      const c = customers[flag.customerId];
      if (!c || byCustomer.has(c.id)) continue;
      byCustomer.set(c.id, {
        id: `alert-decline-${c.id}`,
        alertKind: 'no-deposit-declined',
        customer: c,
        detail: flag.reason
          ? `No-deposit override declined · ${flag.reason}`
          : 'No-deposit override declined; collect a deposit to book',
        whenIso: flag.createdAt,
        action: 'chase',
      });
    }

    // D02-07 / D05-19: a held this-week date another client paid for first. The
    // server released the hold in that pay; the studio's Agent calls back to pick
    // a new date (set before the unpaid-link rows: that link is dead, not unpaid).
    for (const a of buildSlotTakenAlerts({ nowIso: clockIso, deposits, bookings, customers, artists, studioIds: scopedStudios })) {
      const c = customers[a.customerId];
      if (!c || byCustomer.has(c.id)) continue;
      byCustomer.set(c.id, {
        id: `alert-date-taken-${a.depositId}`,
        alertKind: 'date-taken',
        customer: c,
        detail: a.detail,
        whenIso: a.releasedAt,
        action: 'call',
      });
    }

    // Missed calls — agent-lane main items, including a pushed lead whose card
    // kept its hot-lead identity over the missed call (B8).
    for (const item of agentMain) {
      if (item.kind !== 'missed-call' && !item.missedCall) continue;
      if (byCustomer.has(item.customer.id)) continue;
      const last = item.customer.conversation[item.customer.conversation.length - 1];
      byCustomer.set(item.customer.id, {
        id: `alert-missed-${item.customer.id}`,
        alertKind: 'missed-call',
        customer: item.customer,
        detail: item.reason,
        whenIso: last?.timestamp,
        action: 'call',
      });
    }

    // W5 (DECIDED 24 Sep): unpaid deposit links routed to the AGENT (the agent,
    // or a customer self-book link, sent it) — unpaid past 24h -> call back. A
    // Messenger link is an Ops item; a Sweeper link sits on missed-deposits.
    for (const link of buildMissedLinks({ nowIso: clockIso, deposits, bookings, studioIds: scopedStudios })) {
      if (link.owner !== 'agent') continue;
      const c = customers[link.customerId];
      if (!c || byCustomer.has(c.id)) continue;
      byCustomer.set(c.id, {
        id: `alert-unpaid-link-${link.depositId}`,
        alertKind: 'unpaid-link',
        customer: c,
        detail: `$${link.amountAud.toLocaleString()} deposit link unpaid ${link.ageH}h${
          link.sessionIso ? `, ${format(new Date(link.sessionIso), 'EEE d MMM')} session` : ''
        } · ${missedLinkStateLabel(link).toLowerCase()}`,
        whenIso: link.sentAt,
        action: 'call',
      });
    }

    // B4 (75my; Anas 1 Oct Q4): a top-up date move lapsed — the partial wasn't
    // paid, so the new AND the old date were released and the money kept on
    // file. The Agent calls the client back.
    for (const b of lapsedMoveBookings(bookings, clockIso)) {
      if (scopedStudios.length > 0 && !scopedStudios.includes(b.studioId)) continue;
      const c = customers[b.customerId];
      if (!c || byCustomer.has(c.id)) continue;
      byCustomer.set(c.id, {
        id: `alert-move-lapsed-${b.id}`,
        alertKind: 'move-lapsed',
        customer: c,
        detail: `Date move lapsed: the partial wasn't paid, so the ${format(new Date(b.start), 'EEE d MMM')} date was released too. What they've paid is on file.`,
        whenIso: b.cancelledAt,
        action: 'call',
      });
    }

    // B5 (8xar; Anas 1 Oct): a named self-book link expired unpaid. The studio's
    // Agent calls back and sends a FRESH link (never the old one); the alert
    // clears once a new link replaces it or the client is booked again.
    for (const l of Object.values(selfBookLinks)) {
      if (l.status !== 'expired' || l.universal || !l.customerId || l.replacedByLinkId) continue;
      const at = l.expiredAt ?? l.expiresAt;
      if (Date.parse(clockIso) - Date.parse(at) > 14 * 86_400_000) continue;
      if (scopedStudios.length > 0 && !scopedStudios.includes(l.studioId)) continue;
      const c = customers[l.customerId];
      if (!c || c.doNotContact || byCustomer.has(c.id)) continue;
      const bookedSince = Object.values(bookings).some(
        (b) => b.customerId === c.id && isLiveBooking(b) && Date.parse(b.createdAt) > Date.parse(at),
      );
      if (bookedSince) continue;
      byCustomer.set(c.id, {
        id: `alert-self-book-expired-${l.token}`,
        alertKind: 'self-book-expired',
        customer: c,
        detail: `Self-book link expired unpaid (${format(new Date(l.expiresAt), 'EEE d MMM')}). Call back and send a fresh link.`,
        whenIso: at,
        action: 'call',
      });
    }

    // Missed top-ups (B8 / 75nf): the SAME missed-instalment plans the queue
    // chases (not the reminder tracker), so "Chase" always opens the chase.
    for (const item of main) {
      if (!item.topupPlanId) continue;
      const plan = topupPlans[item.topupPlanId];
      const missed = plan?.instalments.find((i) => i.status === 'missed');
      if (!plan || !missed || byCustomer.has(item.customer.id)) continue;
      byCustomer.set(item.customer.id, {
        id: `alert-topup-${item.customer.id}`,
        alertKind: 'missed-topup',
        customer: item.customer,
        detail: `$${missed.amountAud.toLocaleString()} instalment missed ${format(new Date(missed.dueIso), 'EEE d MMM')}${
          plan.chaseAttempts ? ` · ${plan.chaseAttempts}/3 chase sequences` : ''
        }`,
        // bgg4 #9: missed from the end of the due day, not its midnight start.
        whenIso: startOfNextStudioDayIso(missed.dueIso),
        action: 'chase',
      });
    }

    // Anas 8 Oct (enquiries-9): a lead the Messenger pushed is the Agent's for
    // good (after a booking or a red sequence too). A DM the client sent after
    // the push, still unanswered, is the Agent's to answer; after 24h Ops sees
    // it too (opsWorklist's unanswered section).
    for (const c of Object.values(customers)) {
      if (byCustomer.has(c.id)) continue;
      if (scopedStudios.length > 0 && !scopedStudios.includes(c.primaryStudioId)) continue;
      const dm = clientMessagedAfterPush(c, sequenceState[c.id], c.conversation);
      if (!dm) continue;
      byCustomer.set(c.id, {
        id: `alert-client-messaged-${c.id}`,
        alertKind: 'client-messaged',
        customer: c,
        detail: `${REPLY_CHANNEL_LABEL[dm.channel]}: "${dm.body}"`,
        whenIso: dm.timestamp,
        action: 'open',
        channel: dm.channel === 'meta-dm' ? 'meta-dm' : 'ig-dm',
      });
    }

    // New inbound replies — queued leads whose latest message is unanswered.
    for (const { customer: c, entry } of repliesToAction) {
      if (byCustomer.has(c.id)) continue;
      byCustomer.set(c.id, {
        id: `alert-reply-${c.id}`,
        alertKind: 'new-reply',
        customer: c,
        detail: `${REPLY_CHANNEL_LABEL[entry.channel]}: "${entry.body}"`,
        whenIso: entry.timestamp,
        action: 'open',
      });
    }

    // Nurture re-engagement inbound (Phase 2): a reply or call-back on a nurture
    // touch, routed to this studio's agent, carrying its "why" summary.
    for (const inbound of Object.values(nurtureInbounds)) {
      if (inbound.handledAt) continue;
      if (scopedStudios.length > 0 && !scopedStudios.includes(inbound.studioId)) continue;
      const c = customers[inbound.customerId];
      if (!c || byCustomer.has(c.id)) continue;
      byCustomer.set(c.id, {
        id: `alert-nurture-${inbound.id}`,
        alertKind: inbound.kind === 'callback' ? 'nurture-callback' : 'nurture-reply',
        customer: c,
        detail: inbound.summary,
        whenIso: inbound.at,
        action: inbound.kind === 'callback' ? 'call' : 'open',
      });
    }

    // D05-01 (tv-eh2r): oldest first across every type, by each item's own time —
    // the longest-waiting client is worked first. A stamp AHEAD of the clock (an
    // item made before the sim clock was moved back) counts as now: it is never
    // shown as "in 2 days" or sorted behind the future. No time sorts last.
    const nowMs = Date.parse(clockIso);
    const atMs = (r: AlertRow) => {
      const t = r.whenIso ? Date.parse(r.whenIso) : Number.NaN;
      return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
    };
    return [...byCustomer.values()]
      .map((r) => (r.whenIso && Date.parse(r.whenIso) > nowMs ? { ...r, whenIso: clockIso } : r))
      .sort((a, b) => atMs(a) - atMs(b));
  }, [
    agentMain,
    main,
    topupPlans,
    repliesToAction,
    sequenceState,
    deposits,
    bookings,
    customers,
    scopedStudios,
    clockIso,
    noDepositDeclineFlags,
    nurtureInbounds,
    selfBookLinks,
    artists,
  ]);

  // The card an alert is worked from: the customer's queue card, else one built
  // from the alert itself (B8: every alert can join the working set and open the
  // work modal — "Chase" lands on the chase, not the record).
  const itemForAlert = (row: AlertRow): QueueItem => {
    const card =
      main.find((i) => i.customer.id === row.customer.id) ?? scheduledDue.find((i) => i.customer.id === row.customer.id);
    // A fresh alert (a reply, a missed call) makes a cooling-down card workable again.
    const item = card ? { ...card, dormant: false } : alertItem(row);
    // A lost date (D02-07) is worked from the artist's availability: the card
    // opens on "find a slot" to build the new booking.
    return row.alertKind === 'date-taken' ? { ...item, origin: 'date-taken' as const } : item;
  };

  // Work an alert: open its work modal. An expired self-book link (B5, 8xar) is
  // answered with a fresh link, so it opens the generator for that client.
  const onOpenAlert = (row: AlertRow) => {
    if (row.alertKind === 'self-book-expired') {
      navigate('/self-book-generator', { state: { customerId: row.customer.id } });
      return;
    }
    onOpen(itemForAlert(row), row.channel ? { channel: row.channel } : undefined);
  };

  // "Add to working set now" (B8 / 75nf, Anas Q1): every current alert goes to
  // the TOP of the working set, above the six-item cap, until it is worked (an
  // alert that clears drops out on its own). Kept per tab session.
  const [promotedIds, setPromotedIds] = useState<string[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(PROMOTED_KEY) ?? '[]') as string[];
    } catch {
      return [];
    }
  });
  const promotedItems = useMemo(() => {
    // Oldest first by the card's own time (D05-01), a future stamp counting as now.
    const nowMs = Date.parse(clockIso);
    const cardMs = (i: QueueItem) => {
      const t = i.recommendedAt ? Date.parse(i.recommendedAt) : Number.NaN;
      return Number.isFinite(t) ? Math.min(t, nowMs) : Number.MAX_SAFE_INTEGER;
    };
    const promoted = new Set(promotedIds);
    return alerts
      .filter((row) => promoted.has(row.customer.id))
      .map(itemForAlert)
      .sort((a, b) => cardMs(a) - cardMs(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotedIds, alerts, main, scheduledDue, clockIso]);

  // The finite working set: the first WORKING_SET_SIZE of the active leads.
  // Actioning an item drops it from the derived list, so slicing again reveals
  // the next tail item — "reveal on clear", keeping ~6 in view (bd tv-6s3).
  const workingSet = useMemo(() => {
    if (seqTab === 'reds') return activeForTab.slice(0, WORKING_SET_SIZE);
    const promoted = new Set(promotedItems.map((i) => i.customer.id));
    return [...promotedItems, ...activeForTab.filter((i) => !promoted.has(i.customer.id)).slice(0, WORKING_SET_SIZE)];
  }, [activeForTab, promotedItems, seqTab]);
  const lockedCount = Math.max(
    0,
    activeForTab.length - workingSet.filter((i) => activeForTab.some((a) => a.customer.id === i.customer.id)).length,
  );

  // A call sequence is "cleared" once every active lead is contacted (none left
  // to call) but the agent actually worked leads into it (bd tv-1x7).
  const clearedSequence =
    !gapFilter &&
    !showAlerts &&
    seqTab !== 'reds' &&
    activeForTab.length === 0 &&
    promotedItems.length === 0 &&
    completedForTab.length > 0;

  // Auto-advance only the first time a cleared sequence is reached. Once a
  // sequence has advanced, returning to it (manually, or after fresh leads land
  // and clear again) shows the cleared banner but never auto-advances again.
  const willAutoAdvance = clearedSequence && seqTab !== 'third' && !autoAdvanced[seqTab];

  // Automatic hand-off: after the success banner shows, load the next sequence
  // (First -> Second -> Third). The third doesn't auto-advance; it offers the
  // this week's 3+ gap board instead.
  useEffect(() => {
    if (!willAutoAdvance) return;
    const from = seqTab;
    const next: SeqTab = from === 'first' ? 'second' : 'third';
    const timer = setTimeout(() => {
      setAutoAdvanced((prev) => ({ ...prev, [from]: true }));
      setSeqTab(next);
    }, HANDOFF_DELAY_MS);
    return () => clearTimeout(timer);
  }, [willAutoAdvance, seqTab]);


  // Finishable-batch header tiles: leads still to call vs. those already worked
  // this sequence (contacted, now in their 24h cooldown).
  const toCallCount = agentMain.filter((q) => !q.dormant && !q.redKind).length;
  const doneCount = agentMain.filter((q) => q.dormant).length;
  const filterArtist = gapFilter ? artists[gapFilter.artistId] : null;

  const onSelectCell = (cell: HeatmapCell) => {
    setGapFilter({ artistId: cell.artistId, dayIso: cell.dayIso });
  };

  // Every dialer opens the work modal instead of navigating. Voice opens the
  // modal but leaves the composer channel inferred (the card's voice button
  // already starts the call); other channels focus the composer on that channel.
  const onOpen = (item: QueueItem, opts?: { channel?: QueueChannel }) => {
    setModalItem(item);
    setModalChannel(opts?.channel && opts.channel !== 'voice' ? opts.channel : undefined);
  };

  const onSwitchSeqTab = (key: SeqTab) => {
    setSeqTab(key);
    setShowAlerts(false);
  };

  // Batched promotion (bd tv-3wr.2): pull the collected alerts into the working
  // set and drop the agent back onto the First tab to work them.
  const promoteAlerts = () => {
    const ids = alerts.map((row) => row.customer.id);
    setPromotedIds(ids);
    try {
      sessionStorage.setItem(PROMOTED_KEY, JSON.stringify(ids));
    } catch {
      /* private window: the promotion lasts this render session */
    }
    setShowAlerts(false);
    setSeqTab('first');
  };

  if (agentGaps) {
    return (
      <section className="px-10 py-8" data-testid="operator-queue-screen">
        <button
          type="button"
          onClick={() => setAgentGaps(false)}
          className="btn-quiet text-[11px] mb-2"
          data-testid="exit-agent-gaps"
        >
          &lsaquo; My queue
        </button>
        <SweeperWorkspace lane="agent" studioIds={scopedStudios} />
      </section>
    );
  }

  return (
    <section className="px-10 py-8" data-testid="operator-queue-screen">
      <header className="max-w-6xl flex items-start justify-between gap-6">
        <div>
          <h1 className="heading-display text-3xl text-cream">My Queue</h1>
          <p className="font-serif text-cream-soft mt-2 text-base">
            Calling leads to book from next week on.
          </p>
        </div>
        <div className="flex gap-3 text-right">
          <StatTile label="To call" value={toCallCount} accent testid="stat-to-call" />
          <StatTile label="Done" value={doneCount} testid="stat-done" />
        </div>
      </header>

      <div className="mt-8 grid grid-cols-12 gap-6 max-w-screen-2xl">
        <div className="col-span-12 lg:col-span-7">
          {gapFilter ? (
            <>
              <div
                data-testid="gap-fill-banner"
                className="rounded-lg border border-mint/40 bg-mint/5 px-4 py-3 mb-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Target size={18} className="text-mint shrink-0" />
                  <div>
                    <p className="label-small-caps text-mint">Filling a gap</p>
                    <p className="font-serif text-cream mt-1">
                      {filterArtist?.displayName ?? gapFilter.artistId} ·{' '}
                      {format(new Date(gapFilter.dayIso), 'EEE d MMM')}. Ranked by best match for
                      this slot.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setGapFilter(null)}
                  className="btn-quiet text-[11px]"
                  data-testid="exit-gap-fill"
                >
                  <XCircle size={12} /> Exit gap-fill
                </button>
              </div>
              <ArtistDaySchedule artistId={gapFilter.artistId} dayIso={gapFilter.dayIso} />
              <div
                data-testid="gap-fill-coaching"
                className="font-serif text-sm text-cream-soft mb-3 px-1 leading-relaxed"
              >
                Top of the list has the strongest signals AND open calendars near this
                gap, easiest to close in time. Further down sits the reactivation tail
                bigger lift, bigger value if you land it. Click &quot;Why this match?&quot; on any
                row to see the reasoning.
              </div>
              <QueueDialer
                items={queue}
                onOpen={onOpen}
                emptyState="No strong candidates for this gap yet. Try a broader segment from Reactivation."
              />
            </>
          ) : (
            <>
              <div className="mb-4 space-y-2">
                <div className="flex items-center gap-1" data-testid="queue-seq-tabs">
                  {SEQ_TABS.map((tab) => {
                    const active = !showAlerts && seqTab === tab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => onSwitchSeqTab(tab.key)}
                        className={clsx(
                          'px-3 py-1.5 text-xs rounded-md border transition',
                          tab.key === 'reds' && !active
                            ? 'border-blood/40 text-blood/90 hover:border-blood'
                            : active && tab.key === 'reds'
                              ? 'border-blood text-blood bg-blood/10'
                              : active
                                ? 'border-gold-bright text-gold-bright bg-gold/10'
                                : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/40',
                        )}
                        data-testid={`queue-seq-${tab.key}`}
                      >
                        {tab.label}{' '}
                        <span className="ml-1 font-mono text-[10px] text-cream-faint">
                          {seqCounts[tab.key]}
                        </span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setShowAlerts(true)}
                    className={clsx(
                      'px-3 py-1.5 text-xs rounded-md border transition',
                      showAlerts
                        ? 'border-blood text-blood bg-blood/10'
                        : alerts.length > 0
                          ? 'border-blood/50 text-blood hover:border-blood'
                          : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/40',
                    )}
                    data-testid="queue-alerts-tab"
                  >
                    Alerts{' '}
                    {alerts.length > 0 ? (
                      <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-blood px-1.5 py-px font-mono text-[10px] font-semibold text-cream align-middle">
                        <span className="w-1.5 h-1.5 rounded-full bg-cream/90 motion-safe:animate-pulse" />
                        {alerts.length}
                      </span>
                    ) : (
                      <span className="ml-1 font-mono text-[10px] text-cream-faint">0</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAgentGaps(true)}
                    className="ml-auto px-3 py-1.5 text-xs rounded-md border border-gold-dim/30 text-cream-soft hover:border-gold-bright/40 transition"
                    data-testid="queue-agent-gaps"
                  >
                    This week · 3+ gaps
                  </button>
                </div>
              </div>

              {showAlerts ? (
                <div className="space-y-4" data-testid="queue-alerts">
                  {alerts.length > 0 ? (
                    <div
                      className="flex items-center gap-3 rounded-lg border border-gold-dim/30 bg-gold/5 px-4 py-3"
                      data-testid="queue-alerts-banner"
                    >
                      <Bell size={16} className="text-gold-bright shrink-0" />
                      <p className="font-serif text-sm text-cream-soft flex-1">
                        <span className="text-cream font-semibold">
                          {alerts.length} new since your last batch
                        </span>{' '}
                        — they&rsquo;ll join your working set when you clear it.
                      </p>
                      <button
                        type="button"
                        onClick={promoteAlerts}
                        className="shrink-0 rounded-md border border-gold-dim/40 px-3 py-1.5 text-[11px] font-semibold text-cream-soft transition hover:border-gold-bright hover:text-cream"
                        data-testid="queue-alerts-promote"
                      >
                        Add to working set now
                      </button>
                    </div>
                  ) : null}

                  {alerts.length === 0 ? (
                    <p
                      className="panel-dark p-6 text-center font-serif text-cream-soft"
                      data-testid="queue-alerts-empty"
                    >
                      Nothing new. Missed calls, missed top-ups and fresh replies land here as
                      they happen.
                    </p>
                  ) : (
                    <ul>
                      {alerts.map((row) => (
                        <AlertItem key={row.id} row={row} onAction={() => onOpenAlert(row)} />
                      ))}
                    </ul>
                  )}

                  <p
                    className="flex items-center gap-2 font-serif text-xs text-cream-faint pt-1"
                    data-testid="queue-alerts-footer"
                  >
                    <Clock size={13} className="text-cream-faint shrink-0" />
                    Alerts collect here as they happen and join your working set in batches, so
                    calls aren&rsquo;t interrupted.
                  </p>
                </div>
              ) : (
              <>
              {scheduledDue.length > 0 ? (
                <section
                  className="mb-4 rounded-lg border border-gold-bright/50 bg-gold/5 p-3 animate-screen-pop-in animate-ring-pulse"
                  data-testid="queue-scheduled-due"
                >
                  <p className="label-small-caps text-gold-bright mb-2">
                    Scheduled call or message · due in 15 min
                  </p>
                  <QueueDialer items={scheduledDue} onOpen={onOpen} />
                </section>
              ) : null}

              {seqTab === 'reds' ? (
                <div className="space-y-4" data-testid="queue-reds">
                  <p className="font-serif text-xs text-cream-faint" data-testid="queue-light-reds-note">
                    Light reds didn&rsquo;t pick up after three sequences. They recycle to nurture by
                    SMS or IG; call them if you have a moment.
                  </p>
                  <QueueDialer
                    items={lightReds}
                    onOpen={onOpen}
                    emptyState="No light reds. Nobody has run out the full call ladder unanswered."
                  />
                  <details
                    className="rounded-lg border border-blood/30 bg-blood/5 px-4 py-3"
                    data-testid="queue-dark-reds"
                  >
                    <summary className="cursor-pointer font-inter text-[11px] uppercase tracking-widest text-blood/90">
                      Declined · handed to nurture{' '}
                      <span className="font-mono text-cream-faint">{darkReds.length}</span>
                    </summary>
                    <p
                      className="font-serif text-sm text-cream-soft mt-2 mb-3"
                      data-testid="queue-dark-reds-banner"
                    >
                      Kept in on a light-touch email (shop news, not a booking push). Email nurture
                      isn&rsquo;t switched on yet.
                    </p>
                    <QueueDialer
                      items={darkReds}
                      onOpen={onOpen}
                      emptyState="Nobody has said no yet."
                    />
                  </details>
                </div>
              ) : clearedSequence ? (
                <>
                  <HandoffBanner
                    from={seqTab}
                    auto={willAutoAdvance}
                    onGaps={() => setAgentGaps(true)}
                  />
                  <CompletedSequenceDrawer
                    items={completedForTab}
                    stage={seqTab}
                    nowIso={clockIso}
                    onOverride={onOpen}
                  />
                </>
              ) : (
                <>
                  {workingSet.length > 0 ? (
                    <div className="mb-3 space-y-1.5" data-testid="queue-working-set">
                      <p className="font-serif text-xs text-cream-soft">
                        Working set —{' '}
                        <span className="text-cream font-semibold">
                          {workingSet.length} of {Math.max(activeForTab.length, workingSet.length)}
                        </span>
                        {lockedCount > 0 ? ' · the rest unlock as you clear these' : ''}
                      </p>
                      <div className="h-1 rounded-full bg-ink-soft overflow-hidden">
                        <div
                          className="h-full bg-gold-bright rounded-full transition-all"
                          style={{
                            width: `${(workingSet.length / Math.max(activeForTab.length, workingSet.length)) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  ) : null}
                  <QueueDialer
                    items={workingSet}
                    onOpen={onOpen}
                    upNext
                    emptyState={
                      completedForTab.length > 0
                        ? 'Every lead in this sequence is contacted — see Completed below.'
                        : 'Nothing sitting in this sequence stage right now.'
                    }
                  />
                  {lockedCount > 0 ? (
                    <p
                      className="mt-3 flex items-center gap-2 font-serif text-xs text-cream-faint"
                      data-testid="queue-working-set-footer"
                    >
                      <Lock size={13} className="text-cream-faint shrink-0" />
                      {lockedCount} more unlock as you work through these
                    </p>
                  ) : null}
                  {completedForTab.length > 0 ? (
                    <CompletedSequenceDrawer
                      items={completedForTab}
                      stage={seqTab}
                      nowIso={clockIso}
                      onOverride={onOpen}
                    />
                  ) : null}
                </>
              )}
              </>
              )}
            </>
          )}
        </div>

        <aside className="col-span-12 lg:col-span-5 lg:sticky lg:top-4 self-start space-y-4">
          {gapFilter ? (
            <ArtistAvailabilityHeatmap
              rows={heatmapRows}
              studios={studios}
              selected={gapFilter}
              onSelect={onSelectCell}
              onClear={() => setGapFilter(null)}
            />
          ) : (
            <>
              <RailPanel
                title="Scheduled calls & messages"
                count={scheduledCalls.length}
                testid="rail-scheduled-calls"
              >
                {scheduledCalls.length === 0 ? (
                  <RailEmpty>Nothing scheduled later today.</RailEmpty>
                ) : (
                  <ul className="space-y-2">
                    {scheduledCalls.map((item) => (
                      <ScheduledRow key={item.id} item={item} />
                    ))}
                  </ul>
                )}
              </RailPanel>

              <div className="panel-dark" data-testid="rail-availability">
                <button
                  type="button"
                  onClick={() => setShowAvailability((v) => !v)}
                  aria-expanded={showAvailability}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                  data-testid="rail-availability-toggle"
                >
                  <span className="label-small-caps text-gold-bright">Show availability</span>
                  {showAvailability ? (
                    <ChevronUp size={14} className="text-cream-soft" />
                  ) : (
                    <ChevronDown size={14} className="text-cream-soft" />
                  )}
                </button>
                {showAvailability ? (
                  <div className="px-3 pb-3">
                    <ArtistAvailabilityHeatmap
                      rows={heatmapRows}
                      studios={studios}
                      selected={gapFilter}
                      onSelect={onSelectCell}
                      onClear={() => setGapFilter(null)}
                    />
                  </div>
                ) : null}
              </div>
            </>
          )}
        </aside>
      </div>

      <QueueWorkModal
        item={modalItem}
        initialChannel={modalChannel}
        onClose={() => {
          // A promoted alert is worked once its card has been opened and closed:
          // it goes back to its normal place (B8, review).
          if (modalItem && promotedIds.includes(modalItem.customer.id)) {
            const ids = promotedIds.filter((id) => id !== modalItem.customer.id);
            setPromotedIds(ids);
            try {
              sessionStorage.setItem(PROMOTED_KEY, JSON.stringify(ids));
            } catch {
              /* private window */
            }
          }
          setModalItem(null);
        }}
      />
    </section>
  );
}

/**
 * Guarantees a QueryClient for `useAvailabilityView` even when this screen
 * mounts standalone (existing unit tests render it directly, without the app
 * root's `<QueryClientProvider>` from main.tsx). Nesting the same shared
 * `queryClient` singleton here is a no-op when an ancestor already supplies
 * it — same client instance, so no duplicate cache.
 */
export default function OperatorQueue() {
  return (
    <QueryClientProvider client={queryClient}>
      <OperatorQueueInner />
    </QueryClientProvider>
  );
}

function RailPanel({
  title,
  count,
  testid,
  children,
}: {
  title: string;
  count: number;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <div className="panel-dark px-4 py-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <p className="label-small-caps text-gold-bright">{title}</p>
        <span className="font-mono text-[10px] text-cream-faint">{count}</span>
      </div>
      {children}
    </div>
  );
}

function RailEmpty({ children }: { children: React.ReactNode }) {
  return <p className="font-serif text-sm text-cream-faint">{children}</p>;
}

function ScheduledRow({ item }: { item: QueueItem }) {
  const isVoice = item.recommendedChannel === 'voice' || Boolean(item.pinnedCallbackAtIso && item.customer.phone);
  // A pushed lead's row sits at its callback time, not its arrival slot (B8).
  const at = item.pinnedCallbackAtIso ?? item.recommendedAt;
  const ChannelIcon = isVoice ? Phone : MessageSquare;
  return (
    <li
      className="flex items-center gap-3 rounded-md border border-gold-dim/25 bg-ink-deep px-3 py-2"
      data-testid={`rail-scheduled-${item.customer.id}`}
    >
      <ChannelIcon size={13} className="text-gold-bright shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-inter text-sm text-cream truncate">
          {item.customer.preferredName ?? `${item.customer.firstName} ${item.customer.lastName}`}
        </p>
        {at ? (
          <p className="font-serif text-[11px] text-cream-soft mt-0.5">
            {isVoice ? 'Call' : 'Message'} at{' '}
            <span className="text-gold">{simTimeShort(at)}</span>
          </p>
        ) : null}
      </div>
      {at ? (
        <span className="font-mono text-[10px] text-cream-faint uppercase tracking-widest shrink-0">
          {relativeFromIso(at)}
        </span>
      ) : null}
    </li>
  );
}

const REPLY_CHANNEL_LABEL: Record<Channel, string> = {
  sms: 'SMS',
  'meta-dm': 'Messenger',
  'ig-dm': 'IG',
  email: 'Email',
  voice: 'Call',
  'web-form': 'Web',
  'walk-in': 'Walk-in',
};

/**
 * Automatic sequence hand-off (bd tv-1x7). When a call sequence is cleared, a
 * success banner shows and the next sequence loads on its own (First -> Second
 * -> Third). After the third there's nothing to auto-load, so it offers the
 * this week's 3+ gap board instead.
 */
function HandoffBanner({
  from,
  auto,
  onGaps,
}: {
  from: SeqTab;
  auto: boolean;
  onGaps: () => void;
}) {
  const isThird = from === 'third';
  const next = from === 'first' ? 'Second' : from === 'second' ? 'Third' : null;
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-mint/40 bg-mint/5 px-4 py-3 mb-4"
      data-testid="queue-handoff-banner"
    >
      <CheckCircle2 size={18} className="text-mint shrink-0" />
      <div className="flex-1 min-w-0">
        {isThird ? (
          <p className="font-serif text-sm text-cream-soft">
            <span className="text-cream font-semibold">All three sequences cleared.</span> You&rsquo;ve
            reached everyone you can for now &mdash; go fill this week&rsquo;s 3+ gap artists.
          </p>
        ) : auto ? (
          <p className="font-serif text-sm text-cream-soft">
            <span className="text-cream font-semibold">{SEQ_LABEL[from]} sequence cleared.</span>{' '}
            Loading {next} sequence&hellip;
          </p>
        ) : (
          <p className="font-serif text-sm text-cream-soft">
            <span className="text-cream font-semibold">{SEQ_LABEL[from]} sequence cleared.</span>{' '}
            Every lead here is contacted. Pick another sequence when you&rsquo;re ready.
          </p>
        )}
      </div>
      {isThird ? (
        <button
          type="button"
          onClick={onGaps}
          className="shrink-0 rounded-md bg-mint px-4 py-2 text-xs font-semibold text-cream transition hover:opacity-90"
          data-testid="queue-handoff-gaps"
        >
          Fill 3+ gaps
        </button>
      ) : auto ? (
        <span
          className="shrink-0 flex items-center gap-2 font-inter text-[11px] uppercase tracking-widest text-mint"
          data-testid="queue-handoff-loading"
        >
          Auto
          <span className="w-1.5 h-1.5 rounded-full bg-mint motion-safe:animate-pulse" />
        </span>
      ) : null}
    </div>
  );
}

// The sequence a dormant lead rolls into next once its 24h cooldown lifts.
const NEXT_SEQ_LABEL: Record<SeqTab, string> = {
  first: 'Second sequence',
  second: 'Third sequence',
  third: 'final follow-up',
  reds: '',
};

// "Xh Ym" until the given ISO instant, relative to now.
function formatCountdown(nowIso: string, dueIso: string): string {
  const ms = new Date(dueIso).getTime() - new Date(nowIso).getTime();
  if (ms <= 0) return 'now';
  const mins = Math.round(ms / 60_000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Contacted leads (dormant, in their 24h cooldown) collect here rather than
 * cluttering the active focus batch (bd tv-jpx). Each shows a countdown to the
 * next sequence and a "Call now" override that reaches them early on purpose.
 */
function CompletedSequenceDrawer({
  items,
  stage,
  nowIso,
  onOverride,
}: {
  items: QueueItem[];
  stage: SeqTab;
  nowIso: string;
  onOverride: (item: QueueItem) => void;
}) {
  const nextLabel = NEXT_SEQ_LABEL[stage];
  return (
    <details
      className="mt-4 rounded-lg border border-gold-dim/25 bg-ink-soft/40 overflow-hidden"
      data-testid="queue-completed-drawer"
    >
      <summary className="cursor-pointer flex items-center gap-2 px-4 py-3 font-inter text-[11px] uppercase tracking-widest text-cream-soft">
        <CheckCircle2 size={14} className="text-mint" />
        Completed this sequence{' '}
        <span className="font-mono text-cream-faint">{items.length}</span>
      </summary>
      <ul>
        {items.map((item) => {
          const name =
            item.customer.preferredName ??
            `${item.customer.firstName} ${item.customer.lastName}`;
          const last = item.customer.conversation[item.customer.conversation.length - 1];
          return (
            <li
              key={item.id}
              className="flex items-center gap-3 px-4 py-3 border-t border-gold-dim/12 opacity-80"
              data-testid={`queue-completed-${item.customer.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-inter text-[14px] text-cream font-semibold truncate">
                    {name}
                  </span>
                  {item.sequenceDueAtIso && nextLabel ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-ink-soft px-2 py-0.5 font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                      <Lock size={10} className="text-gold-bright" />
                      {nextLabel} in{' '}
                      <span className="font-mono text-cream normal-case tracking-normal">
                        {formatCountdown(nowIso, item.sequenceDueAtIso)}
                      </span>
                    </span>
                  ) : null}
                </div>
                <p className="font-serif text-[12px] text-cream-soft mt-0.5 truncate">
                  Contacted{last ? ` · “${last.body}”` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onOverride(item)}
                className="shrink-0 rounded-md border border-gold-dim/40 px-3 py-1.5 text-[11px] font-semibold text-gold-bright transition hover:border-gold-bright"
                data-testid={`queue-completed-override-${item.customer.id}`}
              >
                Call now
              </button>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

const ALERT_ICON: Record<AlertKind, typeof PhoneMissed> = {
  'no-deposit-declined': Ban,
  'missed-call': PhoneMissed,
  'missed-topup': CircleDollarSign,
  'move-lapsed': CircleDollarSign,
  'self-book-expired': CircleDollarSign,
  'date-taken': CalendarX,
  'unpaid-link': CircleDollarSign,
  'new-reply': MessageSquare,
  'client-messaged': MessageSquare,
  'nurture-callback': PhoneMissed,
  'nurture-reply': MessageSquare,
};

function AlertItem({ row, onAction }: { row: AlertRow; onAction: () => void }) {
  const meta = ALERT_META[row.alertKind];
  const Icon = ALERT_ICON[row.alertKind];
  const urgent = row.alertKind !== 'new-reply';
  const name =
    row.customer.preferredName ?? `${row.customer.firstName} ${row.customer.lastName}`;
  return (
    <li
      className="flex items-center gap-4 border-t border-gold-dim/15 py-3.5 first:border-t-0"
      data-testid={`queue-alert-${row.customer.id}`}
    >
      <div
        className={clsx(
          'shrink-0 w-9 h-9 rounded-full grid place-items-center',
          urgent ? 'bg-blood/10' : 'bg-ink-soft',
        )}
      >
        <Icon size={17} className={urgent ? 'text-blood' : 'text-cream-soft'} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
          {meta.label}
        </p>
        <p className="font-serif text-cream mt-0.5 truncate">
          <span className="font-semibold">{name}</span>{' '}
          <span className="text-cream-soft">&mdash; {row.detail}</span>
        </p>
        {row.whenIso ? (
          <p className="font-serif text-[11px] text-cream-faint mt-0.5">
            {relativeFromIso(row.whenIso)}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onAction}
        className={clsx(
          'shrink-0 rounded-md px-4 py-2 text-xs font-semibold transition',
          urgent
            ? 'bg-gold-bright text-ink-black hover:bg-gold'
            : 'border border-gold-dim/40 text-cream-soft hover:border-gold-bright hover:text-cream',
        )}
        data-testid={`queue-alert-action-${row.customer.id}`}
      >
        {meta.action}
      </button>
    </li>
  );
}

function StatTile({
  label,
  value,
  accent,
  testid,
}: {
  label: string;
  value: number;
  accent?: boolean;
  testid?: string;
}) {
  return (
    <div
      data-testid={testid}
      className={clsx(
        'panel-dark px-4 py-2 min-w-[80px]',
        accent ? 'border-gold-bright/50' : '',
      )}
    >
      <p
        className={clsx(
          'font-cinzel text-2xl',
          accent ? 'text-gold-bright' : 'text-cream',
        )}
      >
        {value}
      </p>
      <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-0.5">
        {label}
      </p>
    </div>
  );
}
